import { ActionContext, Module } from "vuex";
import PubNub from "pubnub";
import { IPubnub } from "../services/IPubnub";
import PubNubSubscriptionService from "../services/PubNubSubscription.Service";
import { getItemFromLocalStorage, setItemInLocalStorage } from "../services/LocalStorage";

type BroadcastMessage = {
  timestamp: number;
  message?: string;
};

type PubNubSubscriptionState = {
  pubNubSub: IPubnub | null;
  broadcastChannels: string[] | null;
  pubNubHospitalInstance: PubNub | undefined;
  pubNubChatInstance: PubNub | undefined;
  pubNubBroadcastInstance: PubNub | undefined;
  broadcastMessages: BroadcastMessage[];
  unreadChatMessages: number;
  lastBroadcastMessageReadAt: number;
};

const state = getDefaultState();

function getDefaultState() {
  return {
    pubNubSub: getItemFromLocalStorage({ key: "pubNubSub", parse: true }) || null,
    broadcastChannels: getItemFromLocalStorage({ key: "broadcastChannels", parse: true }) || null,
    pubNubHospitalInstance: undefined,
    pubNubChatInstance: undefined,
    pubNubBroadcastInstance: undefined,
    broadcastMessages: getItemFromLocalStorage({ key: "broadcastMessages", parse: true }) || [],
    unreadChatMessages: 0,
    lastBroadcastMessageReadAt: getItemFromLocalStorage({ key: "lastBroadcastMessageReadAt" }) || -Infinity,
  };
}

const getters = {
  pubNubSub: (state: PubNubSubscriptionState): IPubnub | null => state.pubNubSub,
  broadcastChannels: (state: PubNubSubscriptionState): string[] | null => state.broadcastChannels,
  pubNubHospitalInstance: (state: PubNubSubscriptionState): PubNub | undefined => state.pubNubHospitalInstance,
  pubNubChatInstance: (state: PubNubSubscriptionState): PubNub | undefined => state.pubNubChatInstance,
  pubNubBroadcastInstance: (state: PubNubSubscriptionState): PubNub | undefined => state.pubNubBroadcastInstance,
  broadcastMessages: (state: PubNubSubscriptionState): BroadcastMessage[] => state.broadcastMessages,
  unreadChatMessages: (state: PubNubSubscriptionState): number => state.unreadChatMessages,
  lastBroadcastMessageReadAt: (state: PubNubSubscriptionState): number => state.lastBroadcastMessageReadAt,
  unreadBroadcastMessages: (state: PubNubSubscriptionState): number =>
    state.broadcastMessages?.filter((m: BroadcastMessage) => m.timestamp > state.lastBroadcastMessageReadAt).length ||
    0,
};

const actions = {
  async loadPubNub(context: ActionContext<PubNubSubscriptionState, unknown>) {
    const { pubnub } = await PubNubSubscriptionService.secret();
    context.commit("SET_PUBNUB_SUB", pubnub);

    const { channels } = await PubNubSubscriptionService.broadcastChannels();
    context.commit("SET_BROADCAST_CHANNELS", channels.broadcast);
  },
  subscribeChatChannel(
    context: ActionContext<PubNubSubscriptionState, unknown>,
    data: { uuid: string; channelId: string },
  ) {
    if (context.state.pubNubSub && context.state.pubNubSub.chat && context.state.pubNubSub.chat.subscribe) {
      const pubNubInstance = new PubNub({
        subscribeKey: context.state.pubNubSub.chat.subscribe,
        userId: data.uuid,
      });

      pubNubInstance.subscribe({ channels: [data.channelId] });

      pubNubInstance.addListener({
        message: (m: any) => {
          context.dispatch("Case/chatMessageEvent", { data: m.message }, { root: true });

          context.commit("ADD_UNREAD_CHAT_MESSAGE", 1);
        },
      });

      context.commit("SET_PUBNUB_CHAT_INSTANCE", pubNubInstance);
    }
  },
  unsubscribeChatChannel(context: ActionContext<PubNubSubscriptionState, unknown>, data: { channelId: string }) {
    if (context.state.pubNubChatInstance) {
      context.state.pubNubChatInstance.unsubscribe({
        channels: [data.channelId],
      });
    }
  },
  subscribeBroadcastChannels(context: ActionContext<PubNubSubscriptionState, unknown>, data: { uuid: string }) {
    if (context.state.pubNubSub && context.state.pubNubSub.chat && context.state.pubNubSub.chat.subscribe) {
      const pubNubInstance = new PubNub({
        subscribeKey: context.state.pubNubSub.chat.subscribe,
        userId: data.uuid,
      });

      pubNubInstance.subscribe({
        channels: context.state.broadcastChannels || undefined,
      });

      pubNubInstance.addListener({
        message: (m: any) => {
          if (m && m.message && m.message.message) {
            context.dispatch("addBroadcastMessage", m.message.message);
          }
        },
      });

      context.commit("SET_PUBNUB_BROADCAST_INSTANCE", pubNubInstance);
    }
  },
  unsubscribeBroadcastChannels(context: ActionContext<PubNubSubscriptionState, unknown>) {
    if (context.state.pubNubBroadcastInstance) {
      context.state.pubNubBroadcastInstance.unsubscribe({
        channels: context.state.broadcastChannels || undefined,
      });
    }
  },
  subscribeHospitalsChannels(
    context: ActionContext<PubNubSubscriptionState, unknown>,
    data: { uuid: string; hospitals: any[] },
  ) {
    if (context.state.pubNubSub && context.state.pubNubSub.api && context.state.pubNubSub.api.subscribe) {
      if (data.hospitals) {
        const channels = data.hospitals.reduce((result, hospital) => {
          if (hospital.statusChannelId) {
            result.push(hospital.statusChannelId);
          }
          return result;
        }, []);

        const pubNubInstance = new PubNub({
          subscribeKey: context.state.pubNubSub.api.subscribe,
          userId: data.uuid,
        });

        pubNubInstance.subscribe({
          channels: channels,
        });

        pubNubInstance.addListener({
          message: (m: any) => {
            if (m && m.message && m.message.payload) {
              context.dispatch("TeamSelection/loadHospitalStatuses", m.message.payload, { root: true });
            }
          },
        });

        context.commit("SET_PUBNUB_HOSPITAL_INSTANCE", pubNubInstance);
      }
    }
  },
  unsubscribeHospitalsChannels(context: ActionContext<PubNubSubscriptionState, unknown>) {
    if (context.state.pubNubHospitalInstance) {
      context.state.pubNubHospitalInstance.unsubscribeAll();
    }
  },
  addBroadcastMessage(context: ActionContext<PubNubSubscriptionState, unknown>, data: BroadcastMessage) {
    context.commit("ADD_BROADCAST_MESSAGE", data);
  },
  addUnreadChatMessage(context: ActionContext<PubNubSubscriptionState, unknown>, unread: number) {
    context.commit("ADD_UNREAD_CHAT_MESSAGE", unread);
  },
  resetUnreadChatMessages(context: ActionContext<PubNubSubscriptionState, unknown>) {
    context.commit("RESET_UNREAD_CHAT_MESSAGES");
  },
  setLastBroadcastMessageReadAt(context: ActionContext<PubNubSubscriptionState, unknown>) {
    context.commit("SET_LAST_BROADCAST_MESSAGE_READ_AT");
  },
  async loadBroadcastHistory(context: ActionContext<PubNubSubscriptionState, unknown>) {
    const { data } = await PubNubSubscriptionService.broadcastHistory();

    context.commit("SET_BROADCAST_MESSAGES", data);
  },
  resetState({ commit }: ActionContext<PubNubSubscriptionState, unknown>) {
    commit("RESET_STATE");
  },
  async setupBroadcastMessagesAndSubscriptions(
    context: ActionContext<PubNubSubscriptionState, unknown>,
    data: { uuid?: string; hospitals?: any[] } = {},
  ) {
    const { uuid, hospitals } = data;
    context.dispatch("unsubscribeBroadcastChannels");
    context.dispatch("unsubscribeHospitalsChannels");
    context.dispatch("subscribeBroadcastChannels", { uuid });
    context.dispatch("subscribeHospitalsChannels", { uuid, hospitals });
    await context.dispatch("loadBroadcastHistory");
  },
};

const mutations = {
  SET_PUBNUB_SUB(state: PubNubSubscriptionState, pubNubSub: IPubnub) {
    state.pubNubSub = pubNubSub;
    setItemInLocalStorage({ key: "pubNubSub", value: pubNubSub });
  },
  SET_BROADCAST_CHANNELS(state: PubNubSubscriptionState, channels: string[]) {
    state.broadcastChannels = channels;
    setItemInLocalStorage({ key: "broadcastChannels", value: channels });
  },
  SET_PUBNUB_CHAT_INSTANCE(state: PubNubSubscriptionState, instance: PubNub) {
    state.pubNubChatInstance = instance;
  },
  SET_PUBNUB_BROADCAST_INSTANCE(state: PubNubSubscriptionState, instance: PubNub) {
    state.pubNubBroadcastInstance = instance;
  },
  SET_PUBNUB_HOSPITAL_INSTANCE(state: PubNubSubscriptionState, instance: PubNub) {
    state.pubNubHospitalInstance = instance;
  },
  SET_BROADCAST_MESSAGES(state: PubNubSubscriptionState, messages: BroadcastMessage[]) {
    state.broadcastMessages = messages.sort((a: BroadcastMessage, b: BroadcastMessage) => b.timestamp - a.timestamp);
    setItemInLocalStorage({ key: "broadcastMessages", value: state.broadcastMessages });
  },
  ADD_BROADCAST_MESSAGE(state: PubNubSubscriptionState, data: BroadcastMessage) {
    state.broadcastMessages.unshift(data);
    setItemInLocalStorage({ key: "broadcastMessages", value: state.broadcastMessages });
  },
  ADD_UNREAD_CHAT_MESSAGE(state: PubNubSubscriptionState, unread: number) {
    if (unread) {
      state.unreadChatMessages = unread;
    } else {
      state.unreadChatMessages += 1;
    }
  },
  RESET_UNREAD_CHAT_MESSAGES(state: PubNubSubscriptionState) {
    state.unreadChatMessages = 0;
  },
  SET_LAST_BROADCAST_MESSAGE_READ_AT(state: PubNubSubscriptionState) {
    state.lastBroadcastMessageReadAt = Date.now();
    setItemInLocalStorage({ key: "lastBroadcastMessageReadAt", value: state.lastBroadcastMessageReadAt });
  },
  RESET_STATE(state: PubNubSubscriptionState) {
    const initialState = getDefaultState();

    Object.keys(initialState).forEach((key) => {
      (state as any)[key] = (initialState as any)[key];
    });
  },
};

const store: Module<PubNubSubscriptionState, unknown> = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};

export { getters, actions, mutations };

export default store;
